export type AdditionalHeadersResolver = () => Promise<Record<string, string>>

export interface ApiRequestContext {
  /** 产品编码 */
  productCode: string
  /** 客户端类型 */
  clientType?: string
  /** 客户端平台 */
  clientPlatform?: string
  /** 客户端版本 */
  clientVersion?: string
  /** 额外请求头解析器 */
  resolveAdditionalHeaders?: AdditionalHeadersResolver
}

const DEFAULT_CLIENT_TYPE = 'web'
const DEFAULT_CLIENT_PLATFORM = 'web'
const DEFAULT_CLIENT_VERSION = '1.0.0'
const DEFAULT_LANGUAGE = 'en'
const LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/

function normalizeLanguageTag(value: string | undefined): string | undefined {
  const normalized = value?.trim()
  if (!normalized || !LANGUAGE_TAG_PATTERN.test(normalized)) {
    return undefined
  }

  return normalized
}

function resolveRequestLanguage(): string {
  const documentLanguage = normalizeLanguageTag(
    typeof document !== 'undefined' ? document.documentElement.lang : undefined
  )
  if (documentLanguage) {
    return documentLanguage
  }

  const navigatorLanguage = normalizeLanguageTag(
    typeof navigator !== 'undefined' ? navigator.language : undefined
  )
  if (navigatorLanguage) {
    return navigatorLanguage
  }

  return DEFAULT_LANGUAGE
}

function resolveClientLocale(): string {
  const documentLanguage = normalizeLanguageTag(
    typeof document !== 'undefined' ? document.documentElement.lang : undefined
  )
  if (documentLanguage) {
    return documentLanguage
  }

  const navigatorLanguage = normalizeLanguageTag(
    typeof navigator !== 'undefined' ? navigator.language : undefined
  )
  if (navigatorLanguage) {
    return navigatorLanguage
  }

  return DEFAULT_LANGUAGE
}

function resolveClientTimezone(): string | undefined {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return undefined
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return timezone?.trim() || undefined
}

function setOptionalHeader(
  headers: Record<string, string>,
  key: string,
  value: boolean | number | string | undefined
) {
  if (value === undefined || value === null) {
    return
  }

  const normalized = typeof value === 'string' ? value.trim() : String(value)
  if (!normalized) {
    return
  }

  headers[key] = normalized
}

function buildBrowserContextHeaders(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }

  const headers: Record<string, string> = {}
  const timezone = resolveClientTimezone()

  setOptionalHeader(headers, 'X-Client-Locale', resolveClientLocale())
  setOptionalHeader(headers, 'X-Client-Timezone', timezone)
  setOptionalHeader(
    headers,
    'X-Client-Timezone-Offset',
    Number.isFinite(new Date().getTimezoneOffset()) ? new Date().getTimezoneOffset() : undefined
  )
  setOptionalHeader(headers, 'X-Client-Screen-Width', window.screen?.width)
  setOptionalHeader(headers, 'X-Client-Screen-Height', window.screen?.height)
  setOptionalHeader(headers, 'X-Client-Viewport-Width', window.innerWidth)
  setOptionalHeader(headers, 'X-Client-Viewport-Height', window.innerHeight)
  setOptionalHeader(headers, 'X-Client-Color-Depth', window.screen?.colorDepth)
  setOptionalHeader(headers, 'X-Client-Pixel-Ratio', window.devicePixelRatio)
  setOptionalHeader(
    headers,
    'X-Client-Touch-Points',
    typeof navigator !== 'undefined' ? navigator.maxTouchPoints : undefined
  )
  setOptionalHeader(
    headers,
    'X-Client-Java-Enabled',
    typeof navigator !== 'undefined' && typeof navigator.javaEnabled === 'function'
      ? navigator.javaEnabled()
      : undefined
  )
  setOptionalHeader(headers, 'X-Client-JavaScript-Enabled', true)
  setOptionalHeader(
    headers,
    'X-Client-Cookies-Enabled',
    typeof navigator !== 'undefined' ? navigator.cookieEnabled : undefined
  )

  return headers
}

/**
 * 构建标准客户端请求头
 * 统一承担产品头、客户端标识、语言与设备指纹注入职责
 */
export async function buildStandardClientHeaders(
  context: ApiRequestContext
): Promise<Record<string, string>> {
  const clientVersion = context.clientVersion || DEFAULT_CLIENT_VERSION

  const headers: Record<string, string> = {
    'X-Product-Code': context.productCode,
    'X-Client-Type': context.clientType || DEFAULT_CLIENT_TYPE,
    'X-Client-Platform': context.clientPlatform || DEFAULT_CLIENT_PLATFORM,
    'X-Client-Version': clientVersion,
    'Accept-Language': resolveRequestLanguage(),
  }
  Object.assign(headers, buildBrowserContextHeaders())

  if (context.resolveAdditionalHeaders) {
    const additionalHeaders = await context.resolveAdditionalHeaders()
    Object.assign(headers, additionalHeaders)
  }

  return headers
}
